/**
 * Frontend i18n key conventions.
 *
 * The frontend resolves customer-facing strings via `i18nKey` returned in API
 * responses. This file defines the namespace conventions and the platform-
 * and module-wide keys that backend error responses can refer to safely.
 *
 * Key shape: `<namespace>.<group>.<token>`. Examples:
 *   errors.internal
 *   errors.notFound.route
 *   errors.validation.failed
 *   errors.auth.invalidCredentials
 *   errors.auth.tokenExpired
 *   errors.rateLimit.exceeded
 *
 * Resource-specific keys (e.g. `errors.product.notFound`) live with the
 * resource that owns them and are added as resources gain feature surface.
 *
 * Non-goals:
 * - Storing translation values (the frontend owns UI translations; backend
 *   templates own outbound-message translations).
 * - Defining a generated/strict union of all valid keys (deferred until the
 *   key surface stabilizes).
 */
export const PLATFORM_I18N_KEYS = {
    ERROR_INTERNAL: 'errors.internal',
    ERROR_NOT_FOUND_ROUTE: 'errors.notFound.route',
    ERROR_VALIDATION_FAILED: 'errors.validation.failed',
    ERROR_UNAUTHORIZED: 'errors.auth.unauthorized',
    ERROR_FORBIDDEN: 'errors.auth.forbidden',
    ERROR_TOKEN_EXPIRED: 'errors.auth.tokenExpired',
    ERROR_TOKEN_INVALID: 'errors.auth.tokenInvalid',
    ERROR_RATE_LIMIT_EXCEEDED: 'errors.rateLimit.exceeded',
    ERROR_SERVICE_UNAVAILABLE: 'errors.serviceUnavailable',
};
/**
 * Auth-module i18n keys.
 *
 * The customer-facing wording is intentionally generic (`Invalid phone
 * number or password`) per `BACKEND_SECURITY.md` — error messages must not
 * reveal which credential failed. Frontend translations should follow the
 * same rule.
 */
export const AUTH_I18N_KEYS = {
    INVALID_CREDENTIALS: 'errors.auth.invalidCredentials',
    ACCOUNT_LOCKED: 'errors.auth.accountLocked',
    USER_ALREADY_EXISTS: 'errors.auth.userAlreadyExists',
    USER_NOT_FOUND: 'errors.auth.userNotFound',
    CURRENT_PASSWORD_INCORRECT: 'errors.auth.currentPasswordIncorrect',
    INVALID_REFRESH_TOKEN: 'errors.auth.invalidRefreshToken',
    REFRESH_TOKEN_EXPIRED: 'errors.auth.refreshTokenExpired',
    USER_DEACTIVATED: 'errors.auth.userDeactivated',
    REGISTRATION_DISABLED: 'errors.auth.registrationDisabled',
    REGISTRATION_INVITE_REQUIRED: 'errors.auth.registrationInviteRequired',
    REGISTRATION_INVITE_INVALID: 'errors.auth.registrationInviteInvalid',
    REGISTRATION_INVITE_EXPIRED: 'errors.auth.registrationInviteExpired',
    REGISTRATION_INVITE_CONSUMED: 'errors.auth.registrationInviteConsumed',
    REGISTRATION_INVITE_PHONE_MISMATCH: 'errors.auth.registrationInvitePhoneMismatch',
    REGISTRATION_LEGAL_ACCEPTANCE_REQUIRED: 'errors.auth.registrationLegalAcceptanceRequired',
    PHONE_VERIFICATION_REQUIRED: 'errors.auth.phoneVerificationRequired',
    // Phase 4.1 — verification + password-reset challenge keys.
    //
    // The "expired" / "used" / "wrong-code" cases all collapse into the
    // single `*_INVALID` key per the v3 plan: the response body must not
    // distinguish them (each distinction is an enumeration channel). The
    // `*_TOO_MANY_ATTEMPTS` key fires only when the per-row attempt cap
    // (`AUTH_CHALLENGE_MAX_ATTEMPTS`) is hit on a wrong code; the row is
    // invalidated server-side.
    EMAIL_NOT_SET: 'errors.auth.emailNotSet',
    EMAIL_ALREADY_VERIFIED: 'errors.auth.emailAlreadyVerified',
    PHONE_ALREADY_VERIFIED: 'errors.auth.phoneAlreadyVerified',
    VERIFICATION_CODE_INVALID: 'errors.auth.verificationCodeInvalid',
    VERIFICATION_TOO_MANY_ATTEMPTS: 'errors.auth.verificationTooManyAttempts',
    PASSWORD_RESET_TOKEN_INVALID: 'errors.auth.passwordResetTokenInvalid',
    PASSWORD_RESET_TOO_MANY_ATTEMPTS: 'errors.auth.passwordResetTooManyAttempts',
};
//# sourceMappingURL=keys.js.map